import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../ThemeContext";
import { authAPI } from "../authAPI";

const ForgotPassword = () => {
    const { isDarkMode, toggleTheme } = useTheme();
    const navigate = useNavigate();

    const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const handleSendOTP = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");
        setMessage("");

        const result = await authAPI.sendForgotPasswordOTP(email);

        if (result.success) {
            setStep(2);
            setMessage("OTP sent successfully to your email.");
        } else {
            setError(result.error);
        }
        setIsLoading(false);
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");
        setMessage("");

        const result = await authAPI.verifyResetOTP(email, otp);

        if (result.success) {
            setStep(3);
            setMessage("OTP verified. Please set your new password.");
        } else {
            setError(result.error);
        }
        setIsLoading(false);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");
        setMessage("");

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match");
            setIsLoading(false);
            return;
        }

        const result = await authAPI.resetPassword(email, newPassword, confirmPassword);

        if (result.success) {
            setMessage("Password reset successfully! Redirecting to login...");
            setTimeout(() => {
                navigate("/login");
            }, 2000);
        } else {
            setError(result.error);
        }
        setIsLoading(false);
    };

    return (
        <div
            className={`min-h-screen ${isDarkMode ? "bg-gray-950" : "bg-gray-50"
                } flex items-center justify-center p-4 relative overflow-hidden`}
        >
            {/* Theme Toggle Button */}
            <button
                onClick={toggleTheme}
                className={`fixed top-4 right-4 p-3 rounded-xl ${isDarkMode
                    ? "bg-gray-800 hover:bg-gray-700 text-yellow-400"
                    : "bg-white hover:bg-gray-100 text-gray-700"
                    } transition-all shadow-lg z-50`}
            >
                {isDarkMode ? "☀️" : "🌙"}
            </button>

            <div className="relative w-full max-w-md">
                <div
                    className={`${isDarkMode
                        ? "bg-gray-900/50 border-gray-800"
                        : "bg-white border-gray-200"
                        } backdrop-blur-xl border rounded-2xl p-8 shadow-xl`}
                >
                    <h2
                        className={`text-2xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"
                            } text-center mb-2`}
                    >
                        {step === 1 && "Forgot Password"}
                        {step === 2 && "Verify OTP"}
                        {step === 3 && "Reset Password"}
                    </h2>
                    <p
                        className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                            } text-center mb-8`}
                    >
                        {step === 1 && "Enter your email to receive an OTP"}
                        {step === 2 && `Enter the OTP sent to ${email}`}
                        {step === 3 && "Create a new password"}
                    </p>

                    {/* Feedback Messages */}
                    {error && (
                        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                            {error}
                        </div>
                    )}
                    {message && (
                        <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
                            {message}
                        </div>
                    )}

                    {/* Step 1: Email Form */}
                    {step === 1 && (
                        <form onSubmit={handleSendOTP} className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}>
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className={`w-full px-4 py-3 ${isDarkMode
                                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                                        : "bg-white border-gray-300 text-gray-900"
                                        } border rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20`}
                                    required
                                    placeholder="you@example.com"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-3 rounded-xl font-medium bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white shadow-lg transition-all"
                            >
                                {isLoading ? "Sending..." : "Send OTP"}
                            </button>
                        </form>
                    )}

                    {/* Step 2: OTP Form */}
                    {step === 2 && (
                        <form onSubmit={handleVerifyOTP} className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}>
                                    OTP Code
                                </label>
                                <input
                                    type="text"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    className={`w-full px-4 py-3 text-center text-2xl tracking-widest ${isDarkMode
                                        ? "bg-gray-800 border-gray-700 text-white"
                                        : "bg-white border-gray-300 text-gray-900"
                                        } border rounded-xl focus:outline-none focus:border-cyan-500`}
                                    required
                                    placeholder="000000"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-3 rounded-xl font-medium bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg transition-all"
                            >
                                {isLoading ? "Verifying..." : "Verify OTP"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className={`w-full text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"} hover:underline`}
                            >
                                Back to Email
                            </button>
                        </form>
                    )}

                    {/* Step 3: New Password Form */}
                    {step === 3 && (
                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}>
                                    New Password
                                </label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className={`w-full px-4 py-3 ${isDarkMode
                                        ? "bg-gray-800 border-gray-700 text-white"
                                        : "bg-white border-gray-300 text-gray-900"
                                        } border rounded-xl focus:outline-none focus:border-cyan-500`}
                                    required
                                    placeholder="New Password"
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}>
                                    Confirm Password
                                </label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className={`w-full px-4 py-3 ${isDarkMode
                                        ? "bg-gray-800 border-gray-700 text-white"
                                        : "bg-white border-gray-300 text-gray-900"
                                        } border rounded-xl focus:outline-none focus:border-cyan-500`}
                                    required
                                    placeholder="Confirm Password"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-3 rounded-xl font-medium bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg transition-all"
                            >
                                {isLoading ? "Resetting..." : "Reset Password"}
                            </button>
                        </form>
                    )}

                    <div className="text-center mt-6">
                        <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors">
                            Back to Login
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
